import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth.js'

export interface PromptItem {
  readonly id: string
  readonly question: string
  readonly opensAt: string
  readonly closesAt: string
  readonly createdAt: string
}

/**
 * The manager shape. An employee's payload simply omits managerNote, author and
 * reviewedById — the API strips them (spec §2.3), so they are optional here and
 * the employee view never reads them.
 */
export interface SubmissionItem {
  readonly id: string
  readonly body: string
  readonly status: 'UNDER_REVIEW' | 'ADOPTED' | 'NOT_PURSUED'
  readonly reviewedAt: string | null
  readonly createdAt: string
  readonly promptId: string
  readonly prompt: { readonly id: string; readonly question: string } | null
  readonly managerNote?: string | null
  readonly author?: { readonly id: string; readonly name: string }
}

export function useCurrentPrompt() {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['innovation', 'current-prompt'],
    queryFn: () => api.get<{ prompt: PromptItem | null }>('/api/innovation/prompts/current'),
  })
}

export function useSubmissions(promptId?: string) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['innovation', 'submissions', promptId ?? 'all'],
    queryFn: () =>
      api.get<SubmissionItem[]>(
        promptId === undefined
          ? '/api/innovation/submissions'
          : `/api/innovation/submissions?promptId=${encodeURIComponent(promptId)}`,
      ),
  })
}

export function useSubmitIdea() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { promptId: string; body: string }) =>
      api.post<SubmissionItem>('/api/innovation/submissions', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['innovation'] })
    },
  })
}

export function usePrompts(enabled: boolean) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['innovation', 'prompts'],
    queryFn: () => api.get<PromptItem[]>('/api/innovation/prompts'),
    enabled,
  })
}

export function useCreatePrompt() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { question: string; opensAt: string; closesAt: string }) =>
      api.post<PromptItem>('/api/innovation/prompts', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['innovation'] })
    },
  })
}

export function useReviewSubmission() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; status: 'ADOPTED' | 'NOT_PURSUED'; managerNote?: string }) => {
      const { id, ...body } = input
      return api.post<SubmissionItem>(`/api/innovation/submissions/${encodeURIComponent(id)}/review`, body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['innovation'] })
    },
  })
}

export const STATUS_LABEL: Record<SubmissionItem['status'], string> = {
  UNDER_REVIEW: 'Under review',
  ADOPTED: 'Adopted',
  NOT_PURSUED: 'Not pursued',
}
